import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Modal from "@/components/ui/modal";
import Chip from "@/components/ui/chip";
import { Calendar, Close, Download, Tickets } from "@/components/icons";
import type {
  SupportTicketDetailResponse,
  SupportTicketFile,
  TicketStatusBackend,
} from "@/features/support-tickets";
import {
  SUPPORT_TICKET_STATUS_CHIP_TYPE,
  SUPPORT_TICKET_STATUS_LABELS_TR,
} from "@/features/support-tickets";
import { getFileDownloadUrl } from "@/lib/files.api";
import { clientSupportTicketsApi } from "../../services/client-support-tickets.api";
import styles from "./ClientSupportTicketDetailModal.module.scss";

interface ClientSupportTicketDetailModalProps {
  ticketId: string | null;
  isOpen: boolean;
  onClose: () => void;
}

function formatDate(dateIso: string): string {
  const d = new Date(dateIso);
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${day}.${month}.${year}`;
}

function getFileExtension(file: SupportTicketFile): string {
  if (file.mimeType) {
    const [type, subtype] = file.mimeType.split("/");
    if (type && subtype) return subtype.toUpperCase();
  }
  const name = file.originalName ?? "";
  const dotIndex = name.lastIndexOf(".");
  if (dotIndex !== -1 && dotIndex < name.length - 1) {
    return name.slice(dotIndex + 1).toUpperCase();
  }
  return "DOSYA";
}

function getShortFileName(file: SupportTicketFile): string {
  const name = file.originalName ?? "";
  if (name.length <= 40) return name;
  return `${name.slice(0, 37)}...`;
}

const ClientSupportTicketDetailModal: React.FC<
  ClientSupportTicketDetailModalProps
> = ({ ticketId, isOpen, onClose }) => {
  const { t } = useTranslation();
  const [data, setData] = useState<SupportTicketDetailResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [downloadingFileId, setDownloadingFileId] = useState<string | null>(
    null,
  );

  useEffect(() => {
    if (!isOpen || !ticketId) {
      setData(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    clientSupportTicketsApi
      .getDetail(ticketId)
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch(() => {
        if (!cancelled) setData(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, ticketId]);

  if (!isOpen || !ticketId) return null;

  const status: TicketStatusBackend = data?.status ?? 2;

  const handleDownload = async (file: SupportTicketFile) => {
    try {
      setDownloadingFileId(file.id);
      const url = await getFileDownloadUrl(file.id);
      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = file.originalName || "dosya";
      link.click();
      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Dosya indirilemedi:", error);
    } finally {
      setDownloadingFileId((current) => (current === file.id ? null : current));
    }
  };

  const customHeader = (
    <div className={styles.header}>
      <div className={styles.headerLeft}>
        <span className={styles.headerIcon}>
          <Tickets width={20} height={20} color="#E7212B" />
        </span>
        <h3 className={styles.headerTitle}>
          {t("clientSupportTickets.detail.title")}
        </h3>
      </div>
      <button
        type="button"
        className={styles.headerCloseBtn}
        onClick={onClose}
        aria-label={t("common.close")}
      >
        <Close width={20} height={20} color="#505766" />
      </button>
    </div>
  );

  const bodyContent = loading ? (
    <div className={styles.loading}>
      {t("clientSupportTickets.loading")}
    </div>
  ) : !data ? (
    <div className={styles.loading}>
      {t("clientSupportTickets.detail.notFound")}
    </div>
  ) : (
    <div className={styles.body}>
      <div className={styles.metaRow}>
        <div className={styles.metaLeft}>
          <Calendar width={18} height={18} />
          <div className={styles.metaText}>
            <span className={styles.metaDate}>
              {formatDate(data.createdAt)}
            </span>
            {/* <span className={styles.metaRequestNo}>{data.requestNo}</span> */}
          </div>
        </div>
        <Chip
          type={SUPPORT_TICKET_STATUS_CHIP_TYPE[status]}
          size="small"
          variant="filled"
        >
          {SUPPORT_TICKET_STATUS_LABELS_TR[status]}
        </Chip>
      </div>

      <div className={styles.title}>{data.title}</div>

      {data.clientMessage && (
        <div className={styles.messageBubble}>
          <div className={styles.description}>
            {data.clientMessage.content || data.description}
          </div>
        </div>
      )}

      {data.files && data.files.length > 0 && (
        <>
          <div className={styles.sectionTitle}>
            {t("clientSupportTickets.detail.attachments")}
          </div>
          <ul className={styles.attachmentsList}>
            {data.files.map((file) => (
              <li key={file.id} className={styles.attachmentItem}>
                <div className={styles.attachmentCard}>
                  <div className={styles.attachmentInfo}>
                    <div className={styles.attachmentBadge}>
                      {getFileExtension(file)}
                    </div>
                    <div className={styles.attachmentText}>
                      <span className={styles.attachmentName}>
                        {getShortFileName(file)}
                      </span>
                    </div>
                  </div>
                  <button
                    type="button"
                    className={styles.downloadButton}
                    onClick={() => handleDownload(file)}
                    disabled={downloadingFileId === file.id}
                    aria-label={t("common.download")}
                  >
                    <Download width={18} height={18} />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </>
      )}

      {data.adminReply && (
        <>
          <div className={styles.sectionTitle}>
            {t("clientSupportTickets.detail.adminReply")}
          </div>
          <div className={styles.messageBubble}>
            <div className={styles.description}>{data.adminReply.content}</div>
          </div>
        </>
      )}
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title=""
      customHeader={customHeader}
      customFooter={null}
      size="lg"
    >
      {bodyContent}
    </Modal>
  );
};

export default ClientSupportTicketDetailModal;
